// @flow
import * as React from 'react';
import { useEffect, useState } from 'react';

export const useBodyLockScroll = (lock: boolean) => {
  React.useLayoutEffect(() => {
    if (document.body && lock) {
      document.body.style.overflowY = 'hidden';
      document.body.style.height = '100vh'; //fix no-scrol for safari
      document.body.style.width = '100vw'; //fix no-scrol for safari
      document.body.style.position = 'fixed'; //fix no-scrol for safari
      return () => {
        if (document.body) {
          document.body.style.overflowY = 'initial';
          document.body.style.height = 'initial';
          document.body.style.position = 'initial';
        }
      };
    }
  }, [lock]);
};

export const useInputAutofill = (ref: { current: HTMLInputElement | null }) => {
  const { value, setFalse, setTrue } = useBoolean(false);

  React.useEffect(() => {
    // onChange is not triggered in Chrome with autofill
    // to fix this we use animation on -webkit-autofill and listen to this animation here
    const input = ref.current;

    const disableAutofillOnChange = () => {
      if (input) {
        input.removeEventListener('transitionend', setTrue);
      }
      setFalse();
    };
    if (input) {
      input.addEventListener('transitionend', setTrue); // listen to animation on autofilled input
      input.addEventListener('change', disableAutofillOnChange); // disable detetecting autofill on input change
      input.addEventListener('focus', disableAutofillOnChange); // disable detetecting autofill on focus
    }

    return () => {
      if (input) {
        input.removeEventListener('transitionend', setTrue);
        input.removeEventListener('change', disableAutofillOnChange);
        input.removeEventListener('focus', disableAutofillOnChange);
      }
    };
  }, [ref, setFalse, setTrue]);

  return value;
};

export const useOnClickOutside = (
  ref: { current: ?HTMLElement },
  handler: Function
) => {
  React.useEffect(() => {
    const eventListener = (event: MouseEvent | TouchEvent) => {
      if (
        ref.current &&
        event.target instanceof Node &&
        !ref.current.contains(event.target)
      ) {
        handler(event);
      }
    };
    document.addEventListener('mouseup', eventListener);
    document.addEventListener('touchstart', eventListener);

    return () => {
      document.removeEventListener('mouseup', eventListener);
      document.removeEventListener('touchstart', eventListener);
    };
  }, [ref, handler]);
};

export const useBoolean = (defaultValue: boolean | (() => boolean)) => {
  const [value, setValue] = React.useState<boolean>(defaultValue);
  return {
    value,
    setValue,
    setTrue: React.useCallback(() => setValue(true), []),
    setToggle: React.useCallback(() => setValue((v) => !v), []),
    setFalse: React.useCallback(() => setValue(false), []),
  };
};

export const useBoundingClientRect = () => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = React.useState({});
  React.useLayoutEffect(() => {
    if (ref.current) {
      setRect(ref.current.getBoundingClientRect());
    }
  }, []);

  return { ref, rect };
};

export const useIsPhoenix = () => {
  return __OSG_CONFIG__.isPhoenixOrigin;
};

type UseScriptStatus = 'idle' | 'loading' | 'ready' | 'error';

const cachedScriptStatuses: {
  [key: string]: UseScriptStatus,
} = {};

function getScriptNode(src: string): {
  node: null | HTMLElement,
  status: ?UseScriptStatus,
} {
  const node = document.querySelector(`script[src="${src}"]`);

  const rawStatus = node && node.getAttribute('data-status');
  const status: ?UseScriptStatus =
    rawStatus === 'idle' ||
    rawStatus === 'loading' ||
    rawStatus === 'ready' ||
    rawStatus === 'error'
      ? rawStatus
      : null;

  return {
    node,
    status,
  };
}

export function useScript(
  src: string,
  options?: {
    shouldPreventLoad?: boolean,
    removeOnUnmount?: boolean,
    removeFromCacheOnUnmount?: boolean,
  }
): UseScriptStatus {
  const [status, setStatus] = useState<UseScriptStatus>(() => {
    if (!src || options?.shouldPreventLoad) {
      return 'idle';
    }

    if (typeof window === 'undefined') {
      // SSR Handling - always return 'loading'
      return 'loading';
    }

    return cachedScriptStatuses[src] || 'loading';
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!src || options?.shouldPreventLoad) {
      return;
    }

    const cachedScriptStatus = cachedScriptStatuses[src];
    if (cachedScriptStatus === 'ready' || cachedScriptStatus === 'error') {
      // If the script is already cached, set its status immediately
      setStatus(cachedScriptStatus);
      return;
    }

    // Fetch existing script element by src
    // It may have been added by another instance of this hook
    const script = getScriptNode(src);
    let scriptNode = script.node;

    if (!scriptNode) {
      // Create script element and add it to document body
      scriptNode = document.createElement('script');
      scriptNode.src = src;
      scriptNode.async = true;
      scriptNode.setAttribute('data-status', 'loading');
      // $FlowIgnore
      document.body.appendChild(scriptNode);

      // Store status in attribute on script
      // This can be read by other instances of this hook
      const setAttributeFromEvent = (event: Event) => {
        const scriptStatus: UseScriptStatus =
          event.type === 'load' ? 'ready' : 'error';
        // $FlowIgnore
        scriptNode.setAttribute('data-status', scriptStatus);
      };

      scriptNode.addEventListener('load', setAttributeFromEvent);
      scriptNode.addEventListener('error', setAttributeFromEvent);
    } else {
      // Grab existing script status from attribute and set to state.
      setStatus(script.status || cachedScriptStatus || 'loading');
    }

    // Script event handler to update status in state
    // Note: Even if the script already exists we still need to add
    // event handlers to update the state for *this* hook instance.
    const setStateFromEvent = (event: Event) => {
      const newStatus = event.type === 'load' ? 'ready' : 'error';
      setStatus(newStatus);
      cachedScriptStatuses[src] = newStatus;
    };

    // Add event listeners
    scriptNode.addEventListener('load', setStateFromEvent);
    scriptNode.addEventListener('error', setStateFromEvent);

    // Remove event listeners on cleanup
    // eslint-disable-next-line consistent-return
    return () => {
      if (scriptNode) {
        scriptNode.removeEventListener('load', setStateFromEvent);
        scriptNode.removeEventListener('error', setStateFromEvent);
      }

      if (scriptNode && options?.removeOnUnmount) {
        scriptNode.remove();
      }

      if (scriptNode && options?.removeFromCacheOnUnmount) {
        delete cachedScriptStatuses[src];
      }
    };
  }, [
    src,
    options?.shouldPreventLoad,
    options?.removeOnUnmount,
    options?.removeFromCacheOnUnmount,
  ]);
  /* eslint-disable react-hooks/exhaustive-deps */

  return status;
}
